import React, { FunctionComponent, useState } from "react";
import { Content } from "@iventis/translations/content/typed-content";
import { ExportUrl } from "@iventis/domain-model/model/exportUrl";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { toast } from "@iventis/toasts/src/toast";
import { FormWizardTemplate, Stages } from "@iventis/components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Header4 } from "@iventis/styles";
import { translate } from "@iventis/translations/translation";
import { ScheduleShareOption, ShareScheduleOptions } from "./share-schedule-options";
import { ExportScheduleOptions } from "./export-schedule-options";
import { ScheduleExport } from "./schedule.types";
import { ShareScheduleLink } from "./share-schedule-link";
import { exportScheduleVersionToUrl } from "./schedule-api-functions";

export const ShareScheduleFormWizard: FunctionComponent<{
    close: () => void;
    projectAdmin: boolean;
    exportUrls: ExportUrl[];
    scheduleVersionId: string; // The top level schedule version. Eg: Comp 1
    projectId: string;
}> = ({ close, projectAdmin, exportUrls, scheduleVersionId, projectId }) => {
    const translate = useIventisTranslate();
    const title = (
        <div style={{ margin: "20px", display: "flex", flexDirection: "row", alignItems: "center" }}>
            <FontAwesomeIcon icon={["fas", "share-nodes"]} />
            <Header4 style={{ paddingLeft: "5px" }}>{translate(Content.schedule3.shareOrExport.title)}</Header4>
        </div>
    );

    const [shareOption, setShareOption] = useState(ScheduleShareOption.LINK);

    const [currentStage, setCurrentStage] = useState(0);

    const [exportScheduleOption, setExportScheduleOption] = useState(ScheduleExport.DOWNLOAD);

    const Stages: Stages = [
        // The intital stage for selecting share or export.
        {
            primaryButtonText: translate(Content.common.buttons.next),
            primaryButtonCallback: () => (shareOption === ScheduleShareOption.LINK ? setCurrentStage(2) : setCurrentStage(1)),
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: close }],
            isValid: true,
            Component: <ShareScheduleOptions selected={shareOption} onSelect={setShareOption} />,
        },
        // Export options stage
        {
            primaryButtonText: translate(Content.schedule2.exports.confirm),
            primaryButtonCallback: () => {
                exportSchedule(exportScheduleOption, scheduleVersionId, exportUrls.length > 0 ? exportUrls[0] : null);
                close();
            },
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.back), onButtonPressed: () => setCurrentStage(0) }],
            isValid: true,
            Component: <ExportScheduleOptions selected={exportScheduleOption} onSelect={setExportScheduleOption} projectAdmin={projectAdmin} exportUrls={exportUrls} />,
        },
        // Retrieve the shared schedule link stage
        {
            primaryButtonText: translate(Content.common.buttons.finish),
            primaryButtonCallback: () => close(),
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.back), onButtonPressed: () => setCurrentStage(0) }],
            isValid: true,
            Component: <ShareScheduleLink translate={translate} scheduleVersionId={scheduleVersionId} projectId={projectId} />,
        },
    ];

    return <>{currentStage != null && <FormWizardTemplate title={title} stages={Stages} currentStage={currentStage} />}</>;
};

const exportSchedule = (exportMethod: ScheduleExport, scheduleVersionId: string, exportUrl: ExportUrl) => {
    switch (exportMethod) {
        case ScheduleExport.URL:
            exportScheduleVersionToUrl(scheduleVersionId, exportUrl.id);
            break;
        case ScheduleExport.DOWNLOAD:
            exportScheduleVersionToUrl(scheduleVersionId, null);
            break;
        default:
            throw new Error("Export method was not recognised");
    }
    toast.info({
        title: translate(Content.common.alert),
        message: translate(Content.schedule3.shareOrExport.exportStarted),
    });
};
